import type { ComponentType } from "react";
import type { IconProps, SvgIconOwnProps, SxProps, Theme } from "@mui/material";
import Icon from "@mui/material/Icon";
import cx from "classnames";

export { default as TimeIcon } from "@mui/icons-material/AccessTime";
export { default as ArrowDropDownIcon } from "@mui/icons-material/ArrowDropDown";
export { default as AspectRatioIcon } from "@mui/icons-material/AspectRatio";
export { default as HackerModeIcon } from "@mui/icons-material/BugReport";
export { default as SchemaIcon } from "@mui/icons-material/CalendarViewWeek";
export { default as CloseIcon } from "@mui/icons-material/Close";
export { default as ContentCopyIcon } from "@mui/icons-material/ContentCopy";
export { default as DeleteIcon } from "@mui/icons-material/Delete";
export { default as DeleteOutlineIcon } from "@mui/icons-material/DeleteOutlined";
export { default as DragIndicatorIcon } from "@mui/icons-material/DragIndicator";
export { default as EditIcon } from "@mui/icons-material/Edit";
export { default as CircledXIcon } from "@mui/icons-material/HighlightOff";
export { default as InfoIcon } from "@mui/icons-material/Info";
export { default as InfoOutlinedIcon } from "@mui/icons-material/InfoOutlined";
export { default as LinkIcon } from "@mui/icons-material/Link";
export { default as MoreVertIcon } from "@mui/icons-material/MoreVert";
export { default as OpenInModalIcon } from "@mui/icons-material/PictureInPicture";
export { default as ReplayIcon } from "@mui/icons-material/Replay";
export { default as SearchIcon } from "@mui/icons-material/Search";
export { default as TableIcon } from "@mui/icons-material/TableRows";
export { default as ColumnIcon } from "@mui/icons-material/ViewColumn";
export { default as VisibilityIcon } from "@mui/icons-material/Visibility";
export { default as VisibilityOffIcon } from "@mui/icons-material/VisibilityOff";
export { default as AirflowIcon } from "@icons/airflow.svg";
export { default as AlertCircleIcon } from "@icons/alert-circle.svg";
export { default as ArrowLeftIcon } from "@icons/arrow-left.svg";
export { default as ArrowRightIcon } from "@icons/arrow-right.svg";
export { default as AsideCollapseExpandIcon } from "@icons/aside-collapse-expand.svg";
export { default as AutoTestIcon } from "@icons/auto-test.svg";
export { default as ChatSparkleIcon } from "@icons/chat-sparkle.svg";
export { default as SLAIcon } from "@icons/clock-backward.svg";
export { default as ClusterIcon } from "@icons/cluster.svg";
export { default as AutoTuneIcon } from "@icons/cog-wrench.svg";
export { default as ColumnAssetIcon } from "@icons/column.svg";
export {
  default as CompareArrowsIcon,
  default as CompareIcon,
} from "@icons/compare.svg";
export { default as DashboardsIcon } from "@icons/dashboard.svg";
export { default as EnlargedDetalisIcon } from "@icons/dashed-square-with-enlarge-magnifying-glass.svg";
export { default as DatasetsIcon } from "@icons/dataset.svg";
export { default as DateTimeIcon } from "@icons/date-time.svg";
export { default as DismissAlertsIcon } from "@icons/dismiss-alerts.svg";
export { default as DistributionIcon } from "@icons/distribution.svg";
export { default as DivergeIcon } from "@icons/diverge.svg";
export { default as EyeIcon } from "@icons/eye.svg";
export { default as FileAssetIcon } from "@icons/file.svg";
export { default as GithubIcon } from "@icons/github.svg";
export { default as EnvIcon } from "@icons/globe.svg";
export { default as OtherMetricTypeIcon } from "@icons/hollow-circle.svg";
export { default as HollowTwinklePlusIcon } from "@icons/hollow-twinkle-plus.svg";
export { default as PitIcon } from "@icons/hourglass.svg";
export { default as FreshnessIcon } from "@icons/leaf.svg";
export { default as LightBulbIcon } from "@icons/lightbulb.svg";
export { default as LineageIcon } from "@icons/lineage.svg";
export { default as AwsLogoIcon } from "@icons/logos/aws.svg";
export { default as AzureIcon } from "@icons/logos/azure.svg";
export { default as DBXIcon } from "@icons/logos/dbx.svg";
export { default as DeltaLakeIcon } from "@icons/logos/delta-lake.svg";
export { default as GcsIcon } from "@icons/logos/gcs.svg";
export { default as BigQueryIcon } from "@icons/logos/google-bigquery-logo.svg";
export { default as HadoopIcon } from "@icons/logos/hadoop.svg";
export { default as HiveIcon } from "@icons/logos/hive.svg";
export { default as OrcIcon } from "@icons/logos/orc.svg";
export { default as ParquetIcon } from "@icons/logos/parquet.svg";
export { default as S3Icon } from "@icons/logos/s3.svg";
export { default as SnowflakeIcon } from "@icons/logos/snowflake.svg";
export { default as ManualTestIcon } from "@icons/manual-test.svg";
export { default as MoneyIcon } from "@icons/money.svg";
export { default as TaskIcon } from "@icons/pad.svg";
export { default as PenFlashIcon } from "@icons/pen-flash.svg";
export { default as PipelineIcon } from "@icons/pipeline.svg";
export { default as ResourcesIcon } from "@icons/resources.svg";
export { default as RunIcon } from "@icons/run.svg";
export { default as VolumeIcon } from "@icons/sigma.svg";
export { default as ParamsIcon } from "@icons/table-with-gear.svg";
export { default as TagIcon } from "@icons/tag.svg";
export { default as TransformationIcon } from "@icons/transformation.svg";
export { default as TriangleWarnIcon } from "@icons/triangle-warn.svg";
export { default as CoverageIcon } from "@icons/umbrella-rain.svg";
export { default as UserIcon } from "@icons/user.svg";
export { default as UsersIcon } from "@icons/users.svg";
export { default as YarnIcon } from "@icons/yarn.svg";

import EqualizerIcon from "@mui/icons-material/Equalizer";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import BellIcon from "@mui/icons-material/NotificationsNone";
import type { ComparativeAnalysisChange } from "src/pages/ComparativeAnalysisPage/ComparativeAnalysisPage.types";
import Checkmark from "@icons/checkmark.svg";
import LightBulbIcon from "@icons/lightbulb.svg";
import SparkIconBase from "@icons/spark.svg";
import StrongExclamation from "@icons/strongExclamation.svg";
import TriangleWarn from "@icons/triangle-warn.svg";
import UpdateAutoTests from "@icons/update-auto-tests.svg";

export type TestStatus = "success" | "warning" | "error" | "info" | null;
export { LightBulbIcon as default };

const statusToBg: Partial<Record<NonNullable<TestStatus>, string>> = {
  success: "bg-success dark:bg-success-light",
  warning: "bg-warning dark:bg-warning-light",
  error: "bg-error dark:bg-error-light",
  info: "bg-primary-light dark:bg-info-light",
};

/**
 * HOC which adds a small round indicator (top-right) to the icon based on a `status` prop
 */
export const withIndicator = <P extends object>(
  Icon: ComponentType<P>,
  { withOutline = true }: { withOutline?: boolean } = {}
): ComponentType<{ status: TestStatus } & Partial<P>> => {
  return ({ status, ...props }: { status: TestStatus } & Partial<P>) => {
    return (
      <div className="relative group">
        {status && (
          <>
            <TestsStatusIcon
              status={status}
              style={{ position: "absolute", top: -8, right: -8, zIndex: 2 }}
              slotProps={{
                inner: {
                  className: "group-hover:animate-ping",
                },
              }}
            />
            <TestsStatusIcon
              status={status}
              style={{ position: "absolute", top: -8, right: -8 }}
              slotProps={{
                inner: {
                  className: withOutline
                    ? "outline-3 outline-background-default"
                    : "",
                },
              }}
            />
          </>
        )}
        <Icon {...(props as P)} />
      </div>
    );
  };
};

type CustomIconsSxPropProps = IconProps & {
  size?: string | number;
  sx?: SxProps<Theme>;
};

/**
 * system-specific mapping of icons names according to business-logic
 */

// CSS-based custom icon (just a circle)

const ASSET_CHANGE_TEXT_COLORS_MAP = {
  added: "success",
  deleted: "error",
  modified: "warning",
} as Record<ComparativeAnalysisChange, string>;

const ASSET_CHANGE_ICON_CODE = {
  added: "+",
  deleted: "–",
  modified: "▪",
} as Record<ComparativeAnalysisChange, string>;

export function AssetChangeIcon({
  change,
  ref,
  ...props
}: Omit<IconProps, "children"> & {
  change: ComparativeAnalysisChange;
  ref?: React.Ref<HTMLSpanElement>;
}) {
  const colorType = ASSET_CHANGE_TEXT_COLORS_MAP[change];

  return (
    <Icon
      {...props}
      className={`inline-grid! place-items-center ${props.className || ""}`}
      ref={ref}
    >
      <div
        style={{
          color: `light-dark(var(--mui-palette-${colorType}-dark), var(--mui-palette-${colorType}-dark))`,
          background: `light-dark(var(--mui-palette-${colorType}-lightest), var(--mui-palette-${colorType}-main))`,
          font: `700 ${change === "modified" ? "10" : "13"}px/0 ${change === "modified" ? "none" : "math"}`,
          // @ts-expect-error TS2353
          textStroke: "1px",
          WebkitTextStroke: "1px",
        }}
        className={cx("h-4 w-4 grid place-items-center")}
      >
        {change && ASSET_CHANGE_ICON_CODE[change]}
      </div>
    </Icon>
  );
}

/**
 * system-specific mapping of icons names according to business-logic
 */

// CSS-based custom icon (just a circle)

export function TestsStatusIcon({
  status,
  ref,
  slotProps,
  ...props
}: Omit<IconProps, "children"> & {
  status: TestStatus;
  ref?: React.Ref<HTMLSpanElement>;
  slotProps?: {
    inner: {
      className?: string;
    };
  };
}) {
  return (
    <Icon
      {...props}
      className={cx("inline-grid! place-items-center", props.className)}
      ref={ref}
    >
      <div
        className={cx(
          `${status ? statusToBg[status] : "bg-neutral-light"} h-2 w-2 rounded-full`,
          slotProps?.inner.className
        )}
      />
    </Icon>
  );
}

export function CheckmarkIcon({
  color = "success",
  ref,
  ...props
}: IconProps & {
  ref?: React.Ref<HTMLSpanElement>;
}) {
  return (
    <Icon {...props} ref={ref} color={color}>
      <Checkmark />
    </Icon>
  );
}

export function SparkIcon(props: CustomIconsSxPropProps) {
  return <SparkIconBase sx={{ color: "#d95807", ...(props.sx ?? {}) }} />;
}

export function UpdateAutoTestIcon({
  ref,
  ...props
}: CustomIconsSxPropProps & {
  ref?: React.Ref<HTMLDivElement>;
}) {
  return (
    <Icon {...props} ref={ref} component="div">
      <UpdateAutoTests viewBox="0.22 0.22 18.05 18.05" />
    </Icon>
  );
}

export function BreakingTestsIcon({
  ref,
  ...props
}: IconProps & {
  ref?: React.Ref<HTMLSpanElement>;
}) {
  return (
    <Icon {...props} ref={ref}>
      <StrongExclamation />
    </Icon>
  );
}

export function NotificationIcon({
  color,
  ref,
  ...props
}: SvgIconOwnProps & {
  ref?: React.Ref<SVGSVGElement>;
}) {
  return <BellIcon color={color} {...props} ref={ref} />;
}

export function MetricsIcon({
  ref,
  ...props
}: SvgIconOwnProps & {
  ref?: React.Ref<SVGSVGElement>;
}) {
  return (
    <EqualizerIcon
      component="svg"
      color={props.color ?? ("neutral" as const)}
      {...props}
      ref={ref}
    />
  );
}

/*
export const AutoTestIcon = forwardRef<SVGSVGElement, SvgIconOwnProps>(
  (props, ref) => (
    <AutoAwesomeIcon
      color={props.color ?? ("neutral" as const)}
      {...props}
      ref={ref}
    />
  )
);

AutoTestIcon.displayName = "AutoTestIcon";
*/
export function TestsIcon({
  ref,
  ...props
}: SvgIconOwnProps & {
  ref?: React.Ref<SVGSVGElement>;
}) {
  return (
    <FactCheckOutlinedIcon
      component="svg"
      color={props.color ?? ("neutral" as const)}
      {...props}
      ref={ref}
    />
  );
}

export function InsightIcon({
  reverse,
  className,
  count = 1,
  lit,
  ref,
  slotProps,
  ...props
}: CustomIconsSxPropProps & {
  reverse?: boolean;
  count?: number;
  lit?: boolean;
  ref?: React.Ref<HTMLDivElement>;
  slotProps?: {
    Icon: IconProps;
  };
}) {
  return (
    <Icon
      component="div"
      ref={ref}
      sx={{ display: "flex" }}
      className={cx(
        "place-items-center items-center gap-1 rounded p-0.5 min-w-5! min-h-5! hover:shadow-primary-light w-fit! whitespace-nowrap bg-primary-lightest dark:bg-primary-dark dark:hover:shadow-primary-dark transition-all duration-100",
        {
          "pr-1.5": count > 1 && !reverse,
          "flex-row-reverse pr-0.5 pl-1.5": count > 1 && reverse,
        },
        className
      )}
      data-test-id="insight-icon"
      {...props}
    >
      <LightBulbIcon
        {...(slotProps?.Icon as SvgIconOwnProps)}
        className={cx("w-max-5! h-max-5! w-auto!", slotProps?.Icon?.className)}
      />
      {count && count > 1 && (
        <span
          className={cx("text-xs leading-none", {
            "-ml-0.5": !reverse,
            "-mr-0.5": reverse,
          })}
        >
          {count}
        </span>
      )}
    </Icon>
  );
}

export const SmallInsightIcon = ({
  className,
  variant = "default",
}: {
  className?: string;
  variant?: "default" | "warning";
}) =>
  variant === "warning" ? (
    // A warning triangle (shape + color) stands out far more than a recolored
    // dot, signalling these insights are less significant than the primary ones.
    <TriangleWarn className={cx("w-3 h-3 text-warning", className)} />
  ) : (
    <div
      className={cx(
        "w-2 h-2 rounded-[2px] border bg-primary-lightest dark:bg-primary-dark border-primary",
        className
      )}
    />
  );
